import { iTokenUser } from '@/atoms/user.atom';
import { isTauri } from '@/lib/offline/isTauri';
import { ROOT_DOMAIN, getBaseDomain, isCustomDomain } from './domain';

// In Tauri the page origin is `tauri://localhost`, so a `.skygis.app`-domained
// cookie can't be set/read. Persist the token in localStorage instead and send
// it as `Authorization: Bearer` (the API authorizer accepts that).
const TAURI_TOKEN_KEY = 'tauri.authToken';

// Custom domains can't read/write `.skygis.app` cookies — persist the token in
// localStorage (same strategy as Tauri) and send it as `Authorization: Bearer`.
const CUSTOM_DOMAIN_TOKEN_KEY = 'customDomain.authToken';
const CUSTOM_DOMAIN_REFRESH_TOKEN_KEY = 'customDomain.refreshToken';

// @ts-ignore
export const TOKEN_KEY = import.meta.env.VITE_LOCAL_STORAGE_TOKEN_KEY || '@actegon.gis';
export const REFRESH_TOKEN_KEY = import.meta.env.VITE_LOCAL_STORAGE_REFRESH_TOKEN_KEY || '@actegon.gis.refresh';

// Each environment keeps its own session. Production (`skygis.app`) cookies
// are scoped to `.skygis.app`, which also covers `*.dev.skygis.app` and
// `*.local.skygis.app` — so the non-production environments scope theirs to
// their own base domain AND use a suffixed name, otherwise prod's cookie would
// shadow theirs (and, before this, dev's JWT overwrote prod's).
// Off our domain (localhost, a LAN IP) a `.skygis.app` Domain is rejected by
// the browser and the session silently never persists, so the cookie is
// host-only there.
// Keep in step with server/api/src/utils/authCookie.ts.
export const authCookieScope = (hostname = window.location.hostname): { suffix: string; domainAttr: string } => {
  const baseDomain = getBaseDomain(hostname);
  const suffix = baseDomain === ROOT_DOMAIN ? '' : `.${baseDomain.slice(0, -(ROOT_DOMAIN.length + 1))}`;
  const ours = hostname === baseDomain || hostname.endsWith(`.${baseDomain}`);
  return { suffix, domainAttr: ours ? `; Domain=.${baseDomain}` : '' };
};
const tokenCookieName = () => `${TOKEN_KEY}${authCookieScope().suffix}`;
const refreshTokenCookieName = () => `${REFRESH_TOKEN_KEY}${authCookieScope().suffix}`;

const getCookieValue = (name: string): string | null => {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  return match.split('=').slice(1).join('=') || null;
};

const setCookie = (name: string, value: string | null, maxAge = 60 * 60 * 24 * 30) => {
  const domain = authCookieScope().domainAttr;
  if (value) {
    document.cookie = `${name}=${value}${domain}; Path=/; Secure; SameSite=Lax; Max-Age=${maxAge}`;
  } else {
    document.cookie = `${name}=${domain}; Path=/; Secure; SameSite=Lax; Max-Age=0`;
  }
};

export const getToken = (): string | null => {
  if (isTauri()) return localStorage.getItem(TAURI_TOKEN_KEY);
  if (isCustomDomain()) return localStorage.getItem(CUSTOM_DOMAIN_TOKEN_KEY);

  const cookieValue = getCookieValue(tokenCookieName());
  if (cookieValue) return cookieValue;
  // Fallback to localStorage for migration
  const lsValue = localStorage.getItem(TOKEN_KEY);
  if (lsValue) {
    setCookie(tokenCookieName(), lsValue);
    localStorage.removeItem(TOKEN_KEY);
    return lsValue;
  }
  return null;
};

export const setToken = (token: string | null) => {
  if (isTauri()) {
    if (token) localStorage.setItem(TAURI_TOKEN_KEY, token);
    else localStorage.removeItem(TAURI_TOKEN_KEY);
    return;
  }
  if (isCustomDomain()) {
    if (token) localStorage.setItem(CUSTOM_DOMAIN_TOKEN_KEY, token);
    else localStorage.removeItem(CUSTOM_DOMAIN_TOKEN_KEY);
    return;
  }
  // Clear any cookie set on the exact hostname (no domain) to prevent shadowing
  document.cookie = `${tokenCookieName()}=; Path=/; Secure; SameSite=Lax; Max-Age=0`;
  setCookie(tokenCookieName(), token);
  localStorage.removeItem(TOKEN_KEY);
};

export const removeToken = () => {
  if (isTauri()) {
    localStorage.removeItem(TAURI_TOKEN_KEY);
    return;
  }
  if (isCustomDomain()) {
    localStorage.removeItem(CUSTOM_DOMAIN_TOKEN_KEY);
    localStorage.removeItem(CUSTOM_DOMAIN_REFRESH_TOKEN_KEY);
    return;
  }
  setCookie(tokenCookieName(), null);
  localStorage.removeItem(TOKEN_KEY);
};

export const getRefreshToken = () => {
  if (isCustomDomain()) return localStorage.getItem(CUSTOM_DOMAIN_REFRESH_TOKEN_KEY);

  const cookieValue = getCookieValue(refreshTokenCookieName());
  if (cookieValue) return cookieValue;
  const lsValue = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (lsValue) {
    setCookie(refreshTokenCookieName(), lsValue);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return lsValue;
  }
  return null;
};

export const setRefreshToken = (token: string) => {
  if (isCustomDomain()) {
    localStorage.setItem(CUSTOM_DOMAIN_REFRESH_TOKEN_KEY, token);
    return;
  }
  setCookie(refreshTokenCookieName(), token);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
export const parseJwt = (token: string): Partial<iTokenUser> & { exp: number } => {
  token = token?.trim?.();
  if (!token) return {} as any;
  const base64Url = token.split('.')[1] || '';
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  try {
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT', error, jsonPayload, token);
    return {} as any;
  }
};

export const getStoredUser = () => {
  const token = getToken();
  if (!token) return null;
  try {
    return parseJwt(token) as iTokenUser;
  } catch (error) {
    return null;
  }
};
